import React, { useState, useEffect } from "react";
import api from "../services/api";
import { format } from "date-fns";
import {
  FiPackage,
  FiCalendar,
  FiRefreshCw,
  FiXCircle,
  FiCheckCircle,
} from "react-icons/fi";

function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const response = await api.get("/subscriptions");
      setSubscriptions(response.data.data || []);
    } catch (err) {
      setError("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const handleCancel = async (subscriptionId) => {
    if (!window.confirm("Are you sure you want to cancel this subscription?")) {
      return;
    }

    setActionLoading(subscriptionId);
    try {
      await api.post(`/subscriptions/${subscriptionId}/cancel`);
      await fetchSubscriptions();
    } catch (err) {
      alert("Failed to cancel subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (subscriptionId) => {
    setActionLoading(subscriptionId);
    try {
      await api.post(`/subscriptions/${subscriptionId}/reactivate`);
      await fetchSubscriptions();
    } catch (err) {
      alert("Failed to reactivate subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: "badge-success",
      trialing: "badge-info",
      past_due: "badge-warning",
      canceled: "badge-error",
      incomplete: "badge-warning",
    };
    return badges[status] || "badge-info";
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Subscriptions</h1>
        <p className="page-subtitle">Manage your active subscriptions</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {subscriptions.length > 0 ? (
        subscriptions.map((sub) => (
          <div key={sub.id} className="subscription-card">
            <div className="subscription-header">
              <div>
                <div className="subscription-name">
                  {sub.product_name || "Subscription"}
                </div>
                <span className={`badge ${getStatusBadge(sub.status)}`}>
                  {sub.status}
                </span>
              </div>
              <div className="subscription-price">
                {formatCurrency(sub.amount, sub.currency)}
                <span>/{sub.billing_period}</span>
              </div>
            </div>

            <div className="subscription-details">
              <div>
                <FiCalendar style={{ marginRight: "4px" }} />
                Current period:{" "}
                {format(new Date(sub.current_period_start), "MMM d")} -{" "}
                {format(new Date(sub.current_period_end), "MMM d, yyyy")}
              </div>
              {sub.cancel_at_period_end && (
                <div style={{ color: "var(--warning)" }}>
                  <FiXCircle style={{ marginRight: "4px" }} />
                  Cancels at period end
                </div>
              )}
            </div>

            {sub.trial_end && new Date(sub.trial_end) > new Date() && (
              <div
                className="alert alert-info mt-4"
                style={{ marginTop: "16px" }}
              >
                🎁 Trial period active until{" "}
                {format(new Date(sub.trial_end), "MMM d, yyyy")}
              </div>
            )}

            <div className="subscription-actions">
              {sub.status === "active" && !sub.cancel_at_period_end && (
                <button
                  className="btn btn-outline"
                  onClick={() => handleCancel(sub.id)}
                  disabled={actionLoading === sub.id}
                >
                  {actionLoading === sub.id
                    ? "Processing..."
                    : "Cancel Subscription"}
                </button>
              )}

              {sub.cancel_at_period_end && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleReactivate(sub.id)}
                  disabled={actionLoading === sub.id}
                >
                  {actionLoading === sub.id
                    ? "Processing..."
                    : "Reactivate Subscription"}
                </button>
              )}

              <button className="btn btn-secondary">
                <FiRefreshCw /> Change Plan
              </button>
            </div>
          </div>
        ))
      ) : (
        <div className="card">
          <div className="empty-state">
            <FiPackage size={48} className="empty-state-icon" />
            <h3 className="empty-state-title">No Subscriptions</h3>
            <p className="empty-state-text">
              You don't have any active subscriptions yet.
            </p>
            <button className="btn btn-primary">Browse Plans</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subscriptions;
